<script>
import {defineComponent, watch} from 'vue'
import IconPlus from '@js/components/Icons/IconPlus.vue'

export default defineComponent({
    name: 'FaqComponent',
    components: {IconPlus},
    props: {
        title: {
            type: String,
            default: 'Частые вопросы'
        },
        subtitle: {
            type: String,
            default: ''
        },
        items: {
            type: Array,
            default: () => [],
            validator: (arr) => (Array.isArray(arr) && arr.every(it => typeof it?.question === 'string' && typeof it?.answer === 'string'))
        },
        schemaEnabled: {
            type: Boolean,
            default: true
        },
        schemaId: {
            type: String,
            default: 'schema-faq-jsonld'
        },
        openFirst: {
            type: Boolean,
            default: true
        },
        wrapperClass: {
            type: String,
            default: 'w-full bg-grayBg rounded-3xl text-black overflow-hidden p-5 md:p-10'
        },
    },
    methods: {
        stripHtml(html) {
            try {
                if (typeof window !== 'undefined') {
                    const el = document.createElement('div')
                    el.innerHTML = html
                    const text = (el.textContent || el.innerText || '').replace(/\s+\n/g, '\n').trim()
                    return text
                }
            } catch (e) { /* no-op */
            }
            // SSR/фолбэк
            return String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
        },
        buildFaqSchema() {
            return {
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: this.items.map((it) => ({
                    '@type': 'Question',
                    name: it.question,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: this.stripHtml(it.answer)
                    }
                }))
            }
        },
        injectSchema() {
            if (!this.schemaEnabled || typeof document === 'undefined') return
            this.removeSchema()
            const script = document.createElement('script')
            script.id = this.schemaId
            script.type = 'application/ld+json'
            script.textContent = JSON.stringify(this.buildFaqSchema())
            document.head.appendChild(script)
        },
        removeSchema() {
            if (typeof document === 'undefined') return
            const el = document.getElementById(this.schemaId)
            if (el) el.remove()
        }
    },
    mounted() {
        this.injectSchema()
    },
    beforeUnmount() {
        this.removeSchema()
    },
    setup(props, {emit}) {
        // Ре-инжектируем схему при изменении данных/флага
        watch(() => [props.schemaEnabled, props.items], () => {
            if (typeof document !== 'undefined') {
                // небольшой тик на случай быстрых последовательных апдейтов
                requestAnimationFrame(() => {
                    const el = document.getElementById(props.schemaId)
                    if (el) el.remove()
                    if (props.schemaEnabled) {
                        const script = document.createElement('script')
                        script.id = props.schemaId
                        script.type = 'application/ld+json'
                        const textOnly = (html) => {
                            try {
                                const d = document.createElement('div');
                                d.innerHTML = html;
                                return (d.textContent || d.innerText || '').trim()
                            } catch (e) {
                                return String(html).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
                            }
                        }
                        script.textContent = JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'FAQPage',
                            mainEntity: props.items.map(it => ({
                                '@type': 'Question',
                                name: it.question,
                                acceptedAnswer: {'@type': 'Answer', text: textOnly(it.answer)}
                            }))
                        })
                        document.head.appendChild(script)
                    }
                })
            }
        }, {deep: true})
    }
})
</script>

<template>
    <section :class="wrapperClass">
        <!-- Заголовки можно переопределить через слот header -->
        <slot name="header">
            <h2 v-if="title" class="text-2xl font-semibold tracking-tight">{{ title }}</h2>
            <p v-if="subtitle" class="mt-2 text-sm text-gray-600 font-medium">{{ subtitle }}</p>
        </slot>

        <div class="mt-6 divide-y divide-gray-200">
            <template v-if="items && items.length">
                <details v-for="(item, idx) in items"
                    :key="idx"
                    class="group py-4"
                    :open="openFirst && idx === 0"
                    @toggle="$emit('toggle', { index: idx, open: $event.target.open })">
                    <summary class="flex cursor-pointer list-none items-start justify-between gap-4">
                        <h3 class="text-base md:text-lg font-medium leading-6 text-gray-900">
                            <!-- Слот вопроса -->
                            <slot name="question" :item="item" :index="idx">{{ item.question }}</slot>
                        </h3>
                        <span aria-hidden="true"
                              class="border border-gray-600 min-w-5 min-h-5 rounded-full flex items-center justify-center transition group-open:rotate-45">
                            <slot name="icon"><IconPlus class="w-2 h-2 transition"/></slot>
                        </span>
                    </summary>
                    <div class="prose prose-sm md:prose-base max-w-none text-gray-700 mt-3">
                        <!-- Слот ответа; по умолчанию используем HTML-строку -->
                        <slot name="answer" :item="item" :index="idx">
                            <div v-html="item.answer"/>
                        </slot>
                    </div>
                </details>
            </template>
            <div v-else class="py-6 text-sm text-gray-500">
                <slot name="empty">Пока здесь пусто.</slot>
            </div>
        </div>

        <!-- Нижний слот (кнопки/CTA и т.п.) -->
        <slot name="footer"/>
    </section>
</template>

<style scoped>
/* Дополнительные стили при необходимости */
</style>
